import React, { ReactElement } from "react";
import {
    ChatSettingsIdentitiesType,
    IntegrationActionType, useGetAllIntegrationsQuery,
} from "../../../__generated__/api";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

type PropClearable<TIsClearable, T> = TIsClearable extends true ? T | undefined : T;

export interface IntegrationsPickerProps<TIsMulti = false, TIsClearable = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    actions?: IntegrationActionType[];
    value: (TIsMulti extends true ? string[] : string) | undefined;
    isClearable?: boolean;
    isInheritable?: boolean;
    identitiesType?: ChatSettingsIdentitiesType;
    canSetNone?: boolean;
    className?: string;
    onSelected: (
        sourceId: PropClearable<TIsClearable, (TIsMulti extends true ? string[] : string)>,
        identitiesType: ChatSettingsIdentitiesType
    ) => void;
}

const NONE_OPTION = {
    label: "None",
    value: ChatSettingsIdentitiesType.None,
};
const INHERIT_OPTION = {
    label: "Inherit from global",
    value: ChatSettingsIdentitiesType.Inherit,
};
const Integrations = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, actions, isClearable, isInheritable, canSetNone, identitiesType,
    className,
}: IntegrationsPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetAllIntegrationsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Integration>[] : Option<Integration>;
    type ValueType = TIsMulti extends true ? Option<Integration>[] : Option<Integration>;

    const handleSelected = (selected: SelectedType) => {
        let values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Integration>)?.value) as Parameters<typeof onSelected>[0];

        let type = ChatSettingsIdentitiesType.None;
        if (Array.isArray(values) && values.isNotEmpty) {
            if (values.includes(ChatSettingsIdentitiesType.None)) {
                type = ChatSettingsIdentitiesType.None;
            } else if (values.includes(ChatSettingsIdentitiesType.Inherit)) {
                type = ChatSettingsIdentitiesType.Inherit;
            } else {
                type = ChatSettingsIdentitiesType.Selected;
            }
        } else if (values != null && values !== "") {
            if (values === ChatSettingsIdentitiesType.None) {
                type = ChatSettingsIdentitiesType.None;
            } else if (values === ChatSettingsIdentitiesType.Inherit) {
                type = ChatSettingsIdentitiesType.Inherit;
            } else {
                type = ChatSettingsIdentitiesType.Selected;
            }
        }

        if (type === ChatSettingsIdentitiesType.None || type === ChatSettingsIdentitiesType.Inherit) {
            values = (isMulti ? [] : undefined) as typeof values;
        }

        onSelected(
            values,
            type,
        );
    };

    let integrations = data?.integrations ?? [];

    type Integration = (typeof integrations)[number];

    if (integrations && actions) {
        const actionSet = new Set(actions);
        integrations = integrations.filter(int => int.actions?.some(action => actionSet.has(action)));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = integrations?.sortBy(row => row.label).map(integration => ({
        label: integration.label as string,
        value: integration.id.toString() as string,
        data: integration,
    })) ?? [];

    const valueMap = values.toIdMap("value") as Record<string, Option<Integration>>;

    let selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    let options: (Option<Integration> | OptionWithChildren<Integration>)[] = values;
    if (canSetNone || isInheritable) {
        const defaultOptions = [];
        if (canSetNone) {
            defaultOptions.push(NONE_OPTION);
            valueMap[NONE_OPTION.value] = NONE_OPTION;
            if (!selected || identitiesType === ChatSettingsIdentitiesType.None) {
                selected = NONE_OPTION as ValueType;
            }
        }
        if (isInheritable) {
            defaultOptions.push(INHERIT_OPTION);
            valueMap[INHERIT_OPTION.value] = INHERIT_OPTION;

            if (identitiesType === ChatSettingsIdentitiesType.Inherit) {
                selected = INHERIT_OPTION as ValueType;
            }
        }

        options = [
            {
                options: defaultOptions,
            },
            {
                label: "Integrations",
                options: values as Option<Integration> [],
            },
        ] as OptionWithChildren<Integration>[];
    }

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            className={className}
            onChange={handleSelected}
            options={(options ?? []) as OptionWithChildren<Integration>[]}
            value={selected as ValueType}
            isClearable={isClearable}
        />
    );
});

Integrations.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    actions: undefined,
    isClearable: undefined,
    isInheritable: false,
    canSetNone: false,
    className: undefined,
    identitiesType: ChatSettingsIdentitiesType.Inherit,
};

export default Integrations as <
    TMulti extends boolean = false,
> (
    props: IntegrationsPickerProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Integrations>;
